import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { getAccessLevel } from '@/utils';
import { mapGetters, mapMutations } from 'vuex';
import AccessLevel from '@/enums/AccessLevel';
import { CHANGE_MY_COVER } from '@/router/routeNames';
import { getPartnerDetailsMP, findPolicyHolder } from '@/utils/MemberUtils';
import Services from '@/services/MyCover/ChangeCover/ChangeProduct';
import MemberDetailsServices from '@/services/MemberDetails';
import { DHLError } from '@general/js/utils';
import { InvalidEmailError } from '@general/js/utils/CustomErrors';

export default {
  data() {
    return {};
  },

  beforeMount() {
    this.enforcePolicyHolderAccess();
  },

  watch: {
    // On a direct URL entry, the memberships (loaded asynchronously by App.vue) and the
    // current policy number (set from the route) become available at different times.
    // Re-run the access check whenever either settles.
    memberships() {
      this.enforcePolicyHolderAccess();
    },
    memberNumber() {
      this.enforcePolicyHolderAccess();
    },
  },

  computed: {
    ...mapGetters({
      members: VUEX_GENERAL.GETTERS.getMembers,
      memberships: VUEX_GENERAL.GETTERS.memberships,
      memberNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      personCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
    }),

    doesPartnerHaveKeepPrivateOn() {
      const partner = getPartnerDetailsMP(this.memberDetails, true);
      if (partner) {
        return Boolean(
          this.members.find((member) => {
            return member.personCode === partner.personCode;
          })?.keepPrivateflag,
        );
      }
      // default fail safe
      return false;
    },

    isPolicyHolderAccess() {
      const accessLevel = getAccessLevel(this.memberNumber, this.memberships);
      return accessLevel?.toLowerCase() === AccessLevel.POLICY_HOLDER_ACCESS.toLowerCase();
    },
  },

  methods: {
    ...mapMutations({
      updateMembers: VUEX_GENERAL.MUTATIONS.updateMembers,
    }),

    /**
     * Only Policy holder access level can access the Change Product flow. This check resides
     * in the common mixin so it is applied for all screens.
     *
     * On a direct URL entry the memberships (loaded asynchronously by App.vue) and the current
     * policy number (set from the route) are populated at different times. If we enforce before
     * both are ready, `memberNumber` may still be its default and `getAccessLevel` returns
     * undefined, incorrectly redirecting a valid policy holder. So we only enforce once the
     * current membership can actually be resolved.
     */
    enforcePolicyHolderAccess() {
      if (!this.memberships || this.memberships.length === 0) {
        return;
      }
      // Wait until the current policy number resolves to an actual membership.
      const currentMembership = this.memberships.find((membership) => {
        return membership.memberNumber === this.memberNumber;
      });
      if (!currentMembership) {
        return;
      }
      if (!this.isPolicyHolderAccess) {
        // if not policy holder, redirect to change my cover landing page
        this.$router.push({ name: CHANGE_MY_COVER });
      }
    },

    doesThisChildHaveKeepPrivateOn(personCode) {
      return Boolean(
        this.members.find((member) => {
          return member.personCode === personCode;
        })?.keepPrivateflag,
      );
    },

    /**
     * Converts an array/list to a string, where values separated with a semi-colon (;)
     * @param {array} array list of items list
     * @returns {String} String with elements separated by semi-colon or an empty string
     */
    convertListToString(array) {
      const formattedList = [];
      array.forEach((item) => {
        formattedList.push(item.display);
      });
      return formattedList.length > 0 ? formattedList.join(';') : '';
    },

    /**
     * Get all existing products on the membership policy (in vuex)
     * @returns Array with all product codes or an empty array
     */
    getExistingProducts() {
      const products = [];
      if (this.membershipProducts) {
        this.membershipProducts.forEach((policy) => {
          products.push(policy.productNumber);
        });
      }
      return products;
    },

    async emailQuoteHook(newEmail = null) {
      const response = await this.emailQuote(this.selectedProducts, this.selectedExcess, this.apiStatus.quoteEmail, newEmail);

      if (response && response.emailId) {
        this.emailSentTo = response.emailId;
      }
    },

    async emailQuote(selectedQuote, selectedExcess, apiStatus, newEmailId = null) {
      try {
        apiStatus.loading = true;
        apiStatus.fail = false;
        apiStatus.success = false;

        const payload = {
          quoteId: selectedQuote.quoteId,
          price: selectedQuote.price,
          excess: selectedExcess,
          frequency: this.paymentFrequency,
        };

        if (newEmailId) {
          payload.newEmailId = newEmailId;
        }

        const response = await Services.postEmailQuote(payload, this.memberNumber);
        if (response.error?.name === "InvalidEmail") {
          throw new InvalidEmailError(response.error.errorMessage);
        };
        apiStatus.success = true;
        apiStatus.isInvalidEmail = false;
        return response;
      } catch (error) {
        logger.log(error.message ?? error);
        apiStatus.fail = true;

        if (error instanceof InvalidEmailError) {
          apiStatus.isInvalidEmail = true;
        }
      } finally {
        apiStatus.loading = false;
      }
      return null;
    },

    async fetchChangeProductRefData(apiStatus, finishLoadingState = true) {
      apiStatus.loading = true;
      apiStatus.fail = false;
      try {
        const response = await Services.getChangeProductReferenceData();
        if (response) {
          this.refData = response;
          this.updateReferenceData(response);
        } else {
          throw new DHLError(`Issue with the response: getChangeProductReferenceData`);
        }
      } catch (error) {
        apiStatus.fail = true;
        logger.log(error.message ?? error);
      } finally {
        if (finishLoadingState) {
          apiStatus.loading = false;
        }
      }
    },

    async fetchMembershipDetailsAPI(apiStatus, coverDetails = null, finishLoadingState = true) {
      apiStatus.loading = true;
      apiStatus.fail = false;

      try {
        // Retrieve cover details if prop not passed in
        const resMembershipDetails = await (coverDetails ? Promise.resolve({ ...this.coverDetails }) : Services.getMembershipDetails(this.memberNumber));
        // Get members in policy if members do not yet exist
        if (!this.members) {
          const allMembers = await MemberDetailsServices.getMembers(this.memberNumber);
          this.updateMembers(allMembers);
        }
        this.updateMemberDetails(resMembershipDetails.members);
        this.updateEligibilityDetails(resMembershipDetails.eligibility);
        this.getPolicyHolderInformation();

        this.updateMembershipProducts(resMembershipDetails.products);
        // checks if current policy is closed hospital and extras product
        this.getClosedHospitalProduct();
        this.getClosedExtrasProduct();

        this.updateMembershipPolicy(resMembershipDetails.membership);
        this.getInitialRebateTierLevel();

        // update fetched values into vuex store
        this.updateValuesIntoVuex();
      } catch (error) {
        apiStatus.fail = true;
        logger.log(error.message ?? error);
      } finally {
        if (finishLoadingState) {
          apiStatus.loading = false;
        }
      }
    },

    /**
     * Grabs the member name, DOB and gender from policy details in Vuex
     */
    getPolicyHolderInformation() {
      if (this.memberDetails) {
        const policyHolder = findPolicyHolder(this.memberDetails, false, true);

        if (policyHolder) {
          // Prefilling form values with policy holder details
          this.changeCoverData.step1.name = policyHolder.firstName;
          this.changeCoverData.step1.DOB = policyHolder.dob;
          // this.changeCoverData.step1.gender = this.convertCodeToValue(this.genderOptions, policyHolder.sex);
          this.changeCoverData.step1.gender = policyHolder.sex;
        }
      }
    },

    updateValuesIntoVuex() {
      this.updateCoverCompositionIntoVuex(); // fetches cover composition
      this.updateCurrentExcessIntoVuex(); // fetches current excess
      this.updatePaymentFrequencyIntoVuex();
    },

    /**
     * Get the existing product codes and excess values from recommendations
     * @param {array} recommendations recommendation list of objects (max of two values). Each object should contain products list
     * @param {array} excessList current selected excess values to be added to product list payload
     * @returns An array with object for each recommendation. Each object contains a list of 'productCodes' and 'excess' value
     */
    getProductList(recommendations, excessList) {
      const productList = [];
      if (recommendations) {
        recommendations.forEach((recommendation, index) => {
          const productCodes = [];
          recommendation.products.forEach((product) => {
            productCodes.push(product.productCode);
          });
          productList.push({
            productCodes,
            excess: excessList[index] ? excessList[index] : '',
          });
        });
      }
      return productList;
    },
  },
};
